import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock3, Search, TrendingDown, TrendingUp, Users, WifiOff } from "lucide-react";
import { apiBaseUrl, authHeaders, formatHm } from "./shared";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
// ~6 months back -- matches the backend's own default range when no
// start/end is passed, same convention as HosCompliance.jsx.
function sixMonthsAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 182);
  return d.toISOString().slice(0, 10);
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Per-load ETA drift episode history from eta_drift_events (persisted
// forever from the same trip_plan/live_eta comparison the Alerts page's
// eta_drift type already computes live, backend/motive_sync.py's
// _check_eta_drift_events) -- unlike that live alert, which vanishes the
// moment a load delivers or its drift resolves, this survives so "which
// loads ran badly behind schedule this month" is answerable after the
// fact. Distinct from the live alert, same as HosCompliance.jsx is
// distinct from Alerts' hos_violation type.
export default function EtaDriftHistory() {
  const navigate = useNavigate();
  const [start, setStart] = useState(sixMonthsAgoIso());
  const [end, setEnd] = useState(todayIso());
  const [data, setData] = useState(null);
  const [syncReliability, setSyncReliability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  // Open-only by default -- "what's drifting right now" is the normal
  // thing to want here; historic/resolved episodes are an explicit,
  // deliberate opt-in via this toggle, not the default view.
  const [openOnly, setOpenOnly] = useState(true);
  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounced(rawSearch, 300);
  const [groupByDispatcher, setGroupByDispatcher] = useState(false);
  const PAGE_SIZE = 25;
  const GROUPED_PAGE_SIZE = 200;
  const effectivePageSize = groupByDispatcher ? GROUPED_PAGE_SIZE : PAGE_SIZE;

  // A date-range/filter change invalidates whatever page we were on.
  useEffect(() => {
    setPage(1);
  }, [start, end, openOnly, search, groupByDispatcher]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = { start, end, page, page_size: effectivePageSize, open_only: openOnly };
    if (search.trim()) params.search = search.trim();
    axios
      .get(`${apiBaseUrl}/scripted/fleet/eta-drift-history`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));

    // Independent, best-effort -- a Motive sync outage during this same
    // window freezes live_eta fleet-wide while it's happening, so a drift
    // episode recorded then may be understating/overstating real drift
    // rather than reflecting a genuinely bad schedule slip. Without this,
    // there was no way to tell "the truck really ran late" apart from
    // "the data just went stale for a while" anywhere on this page.
    axios
      .get(`${apiBaseUrl}/scripted/fleet/motive-sync-reliability`, {
        ...authHeaders(),
        params: { start, end },
      })
      .then((res) => setSyncReliability(res.data))
      .catch(() => setSyncReliability(null));
  }, [start, end, page, effectivePageSize, openOnly, search]);

  const events = data?.events || [];
  const worstOverall = data?.worst_drift_hours_overall ?? 0;

  const groups = useMemo(() => {
    if (!groupByDispatcher) return null;
    const byDispatcher = new Map();
    for (const e of events) {
      const key = e.dispatcher_name || "Unassigned";
      if (!byDispatcher.has(key)) byDispatcher.set(key, []);
      byDispatcher.get(key).push(e);
    }
    return [...byDispatcher.entries()].sort(([a], [b]) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [groupByDispatcher, events]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>ETA Drift History</h1>
          <div style={styles.subtitle}>
            Every load that drifted {data?.drift_threshold_hours ?? 2}h+ from its committed ETA -- not what's
            drifting right now (see Alerts for that), but a real record of how often and how badly it's happened.
          </div>
        </div>
        <div style={styles.dateRange}>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={14} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            type="search"
            placeholder="Search by truck # or driver name..."
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          style={{ ...styles.toggleBtn, ...(openOnly ? styles.toggleBtnActive : {}) }}
          onClick={() => setOpenOnly((v) => !v)}
          title={openOnly ? "Showing only episodes still open right now -- click to include resolved ones too" : "Showing all episodes, including resolved ones"}
        >
          {openOnly ? "Open only" : "Open + resolved"}
        </button>
        <button
          type="button"
          style={{ ...styles.toggleBtn, ...(groupByDispatcher ? styles.toggleBtnActive : {}) }}
          onClick={() => setGroupByDispatcher((v) => !v)}
        >
          <Users size={13} /> Group by dispatcher
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load ETA drift history: {error}</div>}

      {syncReliability?.incident_count > 0 && (
        <div style={styles.syncWarning}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <b>{syncReliability.incident_count}</b> Motive sync outage{syncReliability.incident_count === 1 ? "" : "s"} in
            this range (total {Math.round(syncReliability.total_downtime_minutes)} min down, longest{" "}
            {Math.round(syncReliability.longest_incident_minutes)} min) -- every truck's live ETA was frozen during
            those windows, so a drift episode overlapping one may not reflect real schedule slip. See{" "}
            <Link to="/dispatch/integrations" style={styles.syncWarningLink}>
              Motive Sync Reliability
            </Link>{" "}
            for exact incident times.
          </div>
        </div>
      )}

      {loading && <LoadingState label="Loading ETA drift history…" />}

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{loading ? "…" : data?.events_count ?? 0}</div>
          <div style={styles.summaryLabel}>Drift episodes in range</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: (data?.open_count || 0) > 0 ? "#dc2626" : undefined }}>
            {loading ? "…" : data?.open_count ?? 0}
          </div>
          <div style={styles.summaryLabel}>Still open right now</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: "#dc2626" }}>{loading ? "…" : formatHm(worstOverall)}</div>
          <div style={styles.summaryLabel}>Worst single episode</div>
        </div>
      </div>

      {!loading && !error && events.length === 0 && (
        <div style={styles.emptyState}>
          <Clock3 size={22} color="var(--muted)" />
          <div style={styles.emptyTitle}>
            {rawSearch.trim()
              ? `No episodes match "${rawSearch.trim()}"${openOnly ? " among open ones -- try including resolved episodes too" : ""}.`
              : openOnly
              ? "Nothing is currently drifting"
              : "No ETA drift episodes in this range"}
          </div>
          <div style={styles.emptySubtitle}>
            {rawSearch.trim()
              ? "Try a different truck # or driver name."
              : openOnly
              ? "Every truck is within its committed ETA right now -- switch to \"Open + resolved\" to see historic episodes."
              : `Every load stayed within ${data?.drift_threshold_hours ?? 2}h of its committed ETA.`}
          </div>
        </div>
      )}

      {events.length > 0 && groupByDispatcher && groups && (
        groups.map(([dispatcherName, groupEvents]) => (
          <div key={dispatcherName} style={styles.dispatcherGroup}>
            <div style={styles.dispatcherGroupHead}>
              <span style={styles.dispatcherGroupName}>{dispatcherName}</span>
              <span style={styles.dispatcherGroupCount}>{groupEvents.length} episode{groupEvents.length === 1 ? "" : "s"}</span>
            </div>
            <EventsTable events={groupEvents} navigate={navigate} />
          </div>
        ))
      )}

      {events.length > 0 && !groupByDispatcher && <EventsTable events={events} navigate={navigate} />}

      {data && !groupByDispatcher && (
        <Pagination
          page={data.page || 1}
          totalPages={data.total_pages || 1}
          totalCount={data.total_count ?? events.length}
          pageSize={data.page_size || PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function EventsTable({ events, navigate }) {
  return (
    <div style={styles.table}>
      <div style={styles.headRow}>
        <span style={styles.colTruck}>Truck / Driver</span>
        <span style={styles.colDest}>Destination</span>
        <span style={styles.colNum}>Worst drift</span>
        <span style={styles.colNum}>Duration</span>
        <span style={styles.colOpened}>Opened</span>
        <span style={styles.colStatus}>Status</span>
        <span style={styles.colAction} />
      </div>
      {events.map((e) => {
        const late = (e.worst_drift_hours || 0) > 0;
        return (
          <div key={e.id} style={styles.row}>
            <span style={styles.colTruck}>
              <span style={styles.truckUnit}>{e.truck_unit_number || "—"}</span>
              {e.driver_name && <span style={styles.driverName}> · {e.driver_name}</span>}
            </span>
            <span style={{ ...styles.colDest, color: "var(--muted)" }}>{e.destination || "—"}</span>
            <span style={styles.colNum}>
              {late ? <TrendingDown size={12} style={{ color: "#dc2626" }} /> : <TrendingUp size={12} style={{ color: "#2563eb" }} />}{" "}
              <b style={{ color: late ? "#dc2626" : "#2563eb" }}>{formatHm(Math.abs(e.worst_drift_hours ?? 0))}</b>{" "}
              {late ? "behind" : "ahead"}
            </span>
            <span style={{ ...styles.colNum, color: "var(--muted)" }}>{e.duration_hours != null ? formatHm(e.duration_hours) : "—"}</span>
            <span style={{ ...styles.colOpened, color: "var(--muted)" }}>{fmtDateTime(e.opened_at)}</span>
            <span style={styles.colStatus}>
              <span style={{ ...styles.pill, ...(e.still_open ? styles.pillOpen : styles.pillResolved) }}>
                {e.still_open ? "Open" : "Resolved"}
              </span>
            </span>
            <span style={styles.colAction}>
              {e.truck_id && (
                <button
                  type="button"
                  style={styles.viewBtn}
                  onClick={() => navigate(`/dispatch/operational-center?truck=${e.truck_id}&tab=route`)}
                  title="Open this truck's current load"
                >
                  View <ArrowRight size={12} />
                </button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 640 },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  dateInput: {
    fontSize: 12.5, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  searchWrap: { position: "relative", flex: "1 1 260px", minWidth: 180 },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px 8px 30px",
    borderRadius: 8, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
  },
  toggleBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
    color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 12px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
  },
  toggleBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },

  dispatcherGroup: { marginBottom: 16 },
  dispatcherGroupHead: { display: "flex", alignItems: "baseline", gap: 8, padding: "4px 2px 8px" },
  dispatcherGroupName: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  dispatcherGroupCount: { fontSize: 11.5, color: "var(--muted)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  syncWarning: {
    display: "flex", alignItems: "flex-start", gap: 8,
    background: "rgba(161,98,7,0.07)", border: "1px solid rgba(161,98,7,0.25)", color: "var(--ink-2)",
    borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 12.5, lineHeight: 1.5, marginBottom: 16,
  },
  syncWarningLink: { color: "#a16207", fontWeight: 700, textDecoration: "underline" },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 },
  summaryCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "40px 20px",
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  emptySubtitle: { fontSize: 12.5, color: "var(--muted)" },

  table: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", overflow: "hidden",
  },
  headRow: {
    display: "flex", alignItems: "center", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.3, padding: "10px 14px", borderBottom: "1px solid var(--line)",
  },
  row: {
    display: "flex", alignItems: "center", fontSize: 12.5, color: "var(--ink-2)", padding: "10px 14px",
    borderBottom: "1px solid var(--line)",
  },
  colTruck: { flex: "1.3 1 0", minWidth: 0 },
  colDest: { flex: "1.4 1 0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  colNum: { flex: "1 1 0", textAlign: "right" },
  colOpened: { flex: "1 1 0", textAlign: "right" },
  colStatus: { flex: "0.8 1 0", textAlign: "right" },
  colAction: { width: 76, textAlign: "right", flexShrink: 0 },
  truckUnit: { fontWeight: 700, color: "var(--ink)" },
  driverName: { color: "var(--muted)" },
  pill: { fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: 999 },
  pillOpen: { color: "#dc2626", background: "rgba(220,38,38,0.08)" },
  pillResolved: { color: "var(--muted)", background: "var(--bg)" },
  viewBtn: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
    color: "var(--brand)", background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: 6, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
  },
};
